import React, { useState, useEffect, useCallback } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate, 
  useNavigate
} from 'react-router-dom';
import { axiosInstance } from './config';
import './App.css';
import StaffTable from './components/StaffTable';
import AddStaffForm from './components/AddStaffForm';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import NotificationCenter from './components/NotificationCenter';
import NotificationsPage from './components/NotificationsPage';
// Modified ProtectedRoute component
const ProtectedRoute = ({ children, adminRequired = false }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
  const adminToken = localStorage.getItem('adminToken');
  const userToken = localStorage.getItem('token');

  console.log('ProtectedRoute check:', {
    adminRequired,
    hasUserToken: !!userToken,
    isSystemAdmin,
    hasAdminToken: !!adminToken,
    userRole: user?.role
  });

  // For admin panel access
  if (adminRequired) {
    if (user?.role === 'owner' || (adminToken && isSystemAdmin)) {
      return children;
    }
    return <Navigate to="/" replace />;
  }

  // For regular routes
  if (!userToken && !adminToken) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize authentication state from localStorage
    return !!localStorage.getItem('token');
  });
  const [currentBusiness, setCurrentBusiness] = useState(() => {
    // Initialize business state from localStorage
    const businessId = localStorage.getItem('businessId');
    const businessName = localStorage.getItem('businessName');
    const businessEmail = localStorage.getItem('businessEmail');
    const emailFrequency = localStorage.getItem('businessEmailFrequency');
    const availableCredits = localStorage.getItem('businessAvailableCredits');
    const usedCredits = localStorage.getItem('businessUsedCredits');
    return businessId ? { 
      id: businessId, 
      name: businessName,
      email: businessEmail,
      email_frequency: emailFrequency,
      available_credits: availableCredits,
      used_credits: usedCredits
    } : null;
  });
  const [userRole, setUserRole] = useState(() => localStorage.getItem('userRole'));
  const [activeTab, setActiveTab] = useState('home');
  const [, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Add environment state
  const [userEnvironment, setUserEnvironment] = useState(() => 
    localStorage.getItem('userEnvironment') || 'test'
  );

  useEffect(() => {
    console.log('Current Business State:', currentBusiness);
    console.log('LocalStorage Business:', {
        id: localStorage.getItem('businessId'),
        name: localStorage.getItem('businessName'),
        email: localStorage.getItem('businessEmail'),
        email_frequency: localStorage.getItem('businessEmailFrequency')
    });
  }, [currentBusiness]);

  const handleLogin = async (authData) => {
    try {
      console.log('1. Starting login process');
      setLoading(true);
      console.log('2. Auth Data received:', authData);
      console.log('3. Business Data:', authData.business);
      
      const { token, user, business } = authData;
      
      // Store everything in localStorage based on role
      if (user.role === 'owner') {
        localStorage.clear();
        localStorage.setItem('adminToken', token);
        localStorage.setItem('isSystemAdmin', 'true');
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
      } else {
        localStorage.clear();
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
        localStorage.setItem('userEnvironment', user.environment); // Store environment
        setUserEnvironment(user.environment); // Set environment state
        
        if (business) {
          console.log('4. Setting business data:', business);
          // Store complete business object in localStorage
          localStorage.setItem('business', JSON.stringify(business));
          // Also store individual fields for backward compatibility
          localStorage.setItem('businessId', business.id);
          localStorage.setItem('businessName', business.name);
          localStorage.setItem('businessEmail', business.email);
          localStorage.setItem('businessEmailFrequency', business.email_frequency);
          
          const businessState = {
            id: business.id,
            name: business.name,
            email: business.email,
            email_frequency: business.email_frequency,
            created_at: business.created_at || business.created_date
          };
          console.log('5. Setting business state:', businessState);
          setCurrentBusiness(businessState);
        }
      }
      
      // Set axios default authorization header
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Update state
      setUser(user);
      setUserRole(user.role);
      setIsAuthenticated(true);
      
      // Set loading to false before navigation
      setLoading(false);
      
      // Navigate based on role
      if (user.role === 'owner') {
        navigate('/admin/panel', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
      
    } catch (error) {
      console.error('Error during login:', error);
      setLoading(false);
      alert('Login failed. Please try again.');
    }
  };

  const handleAdminLogin = (adminData) => {
    localStorage.setItem('isSystemAdmin', 'true');
  };

  const handleLogout = useCallback(() => {
    localStorage.clear();
    delete axiosInstance.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setCurrentBusiness(null);
    setUserRole(null);
    setUserEnvironment('test'); // Reset environment to test
    navigate('/');
  }, [navigate]);

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/staff');
      setLoading(false);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching staff:', err);
      setLoading(false);
      return [];
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const canPerformAction = (requiredRole) => {
    if (!userRole) return false;
    if (userRole === 'admin') return true;
    if (userRole === 'manager' && requiredRole !== 'admin') return true;
    if (userRole === 'user' && requiredRole === 'user') return true;
    return false;
  };

  const renderDashboardContent = () => {
    return (
      <div className="dashboard-content">
        {canPerformAction('manager') && (
          <AddStaffForm 
            showForm={showAddForm}
            onToggleForm={() => setShowAddForm(!showAddForm)}
            onStaffAdded={fetchStaffList} 
          />
        )}
        <StaffTable 
          loading={loading} 
          setLoading={setLoading}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          canPerformAction={canPerformAction}
        />
      </div>
    );
  };

  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState(currentBusiness?.email || '');
  const [updateError, setUpdateError] = useState('');

  const handleEmailUpdate = async () => {
    try {
      setUpdateError('');
      const response = await axiosInstance.put(`/api/staff/business/${currentBusiness.id}`, {
        email: newEmail
      });

      if (response.data) {
        // Update local storage and state
        localStorage.setItem('businessEmail', newEmail);
        setCurrentBusiness(prev => ({
          ...prev,
          email: newEmail
        }));
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating business email:', error);
      setUpdateError('Failed to update email. Please try again.');
    }
  };

  // Wrap fetchBusinessDetails in useCallback
  const fetchBusinessDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/staff/business-details/${currentBusiness.id}`);
      console.log('Fetched business details:', response.data);
      
      localStorage.setItem('businessAvailableCredits', response.data.available_credits);
      localStorage.setItem('businessUsedCredits', response.data.used_credits);
      
      setCurrentBusiness(prev => ({
        ...prev,
        available_credits: response.data.available_credits,
        used_credits: response.data.used_credits,
        can_run_manual_checks: response.data.can_run_manual_checks
      }));
    } catch (error) {
      console.error('Error fetching business details:', error);
    }
  }, [currentBusiness?.id]);

  // Wrap fetchBusinessCredits in useCallback
  const fetchBusinessCredits = useCallback(async () => {
    try {
      if (!currentBusiness?.id) return;
      
      console.log('Fetching business credits for ID:', currentBusiness.id);
      const response = await axiosInstance.get(`/api/staff/business-details/${currentBusiness.id}`);
      
      if (response.data) {
        console.log('Business details response:', response.data);
        console.log('Manual checks setting:', response.data.can_run_manual_checks);
        
        localStorage.setItem('businessCanRunManualChecks', response.data.can_run_manual_checks ? 'true' : 'false');
        
        const businessObj = JSON.parse(localStorage.getItem('business') || '{}');
        businessObj.can_run_manual_checks = !!response.data.can_run_manual_checks;
        localStorage.setItem('business', JSON.stringify(businessObj));
        
        setCurrentBusiness(prev => ({
          ...prev,
          can_run_manual_checks: !!response.data.can_run_manual_checks
        }));
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
    }
  }, [currentBusiness?.id]);

  // Update the useEffects to include the callbacks in dependencies
  useEffect(() => {
    if (activeTab === 'settings' && currentBusiness?.id) {
      fetchBusinessDetails();
    }
  }, [activeTab, currentBusiness?.id, fetchBusinessDetails]);

  useEffect(() => {
    if (isAuthenticated && currentBusiness?.id) {
      fetchBusinessCredits();
    }
  }, [isAuthenticated, currentBusiness?.id, fetchBusinessCredits]);

  const renderSettingsContent = () => {
    console.log('Rendering settings with business state:', currentBusiness);
    
    // Calculate remaining credits using parseInt to ensure proper calculation
    const availableCredits = parseInt(currentBusiness?.available_credits) || 0;
    const usedCredits = parseInt(currentBusiness?.used_credits) || 0;
    
    return (
      <div className="settings-content">
        <h2>Settings</h2>
        <div className="settings-grid">
          <div className="setting-item">
            <label>Version Number:</label>
            <span>1.0.0</span>
          </div>
          <div className="setting-item">
            <label>Business Name:</label>
            <span>{currentBusiness?.name}</span>
          </div>
          <div className="setting-item">
            <label>Business Email:</label>
            {isEditing ? (
              <div className="email-edit-container">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="email-input"
                />
                <button onClick={handleEmailUpdate} className="save-btn">Save</button>
                <button onClick={() => setIsEditing(false)} className="cancel-btn">Cancel</button>
                {updateError && <div className="error-message">{updateError}</div>}
              </div>
            ) : (
              <div className="email-display">
                <span>{currentBusiness?.email}</span>
                {canPerformAction('admin') && (
                  <button onClick={() => setIsEditing(true)} className="edit-btn">Edit</button>
                )}
              </div>
            )}
          </div>
          <div className="setting-item">
            <label>Email Frequency:</label>
            <span>{currentBusiness?.email_frequency || 'Not set'}</span>
          </div>
          <div className="setting-item credits-info">
            <label>DVLA Check Credits</label>
            <div className="credits-details">
              <div className="credits-stat">
                <span className="credits-label">Available Credits:</span>
                <span className="credits-value">{availableCredits}</span>
              </div>
              <div className="credits-stat">
                <span className="credits-label">Used Credits:</span>
                <span className="credits-value">{usedCredits}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add this useEffect to monitor authentication and business data
  useEffect(() => {
    const checkAuthAndBusiness = () => {
      const token = localStorage.getItem('token');
      const adminToken = localStorage.getItem('adminToken');
      const businessId = localStorage.getItem('businessId');
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      
      
      // Only check for business data if it's a regular user
      if (token && !adminToken && !businessId && user?.role !== 'owner') {
        console.warn('Token exists but no business data found');
        handleLogout(); // Force logout if data is inconsistent
      }
    };
    
    checkAuthAndBusiness();
  }, [handleLogout]);

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          isAuthenticated ? 
            (userRole === 'owner' ? 
              <Navigate to="/admin/panel" replace /> : 
              <Navigate to="/dashboard" replace />
            ) : 
            <Login 
              onLogin={handleLogin} 
              setUser={setUser}
            />
        } 
      />
      
      <Route
        path="/admin/login"
        element={
          localStorage.getItem('adminToken') ? 
            <Navigate to="/admin/panel" replace /> :
            <AdminLogin 
              onAdminLogin={handleAdminLogin}
              onBackToLogin={() => navigate('/')}
            />
        }
      />

      <Route
        path="/admin/panel"
        element={
          <ProtectedRoute adminRequired={true}>
            <AdminPanel onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <div className="App">
              <header className="app-header">
                <div className="header-content">
                  <h1>
                    {currentBusiness?.name ? 
                      `${currentBusiness.name} - DVLA Staff License Check Dashboard` : 
                      'Loading Dashboard...'}
                  </h1>
                  <div className="nav-links">
                    <div className="environment-indicator">
                      Environment: <span className={userEnvironment}>{userEnvironment.toUpperCase()}</span>
                    </div>
                    {userRole && (
                      <span className="user-role">Role: {userRole}</span>
                    )}
                    <NotificationCenter />
                    <button className="logout-btn" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              </header>
              <div className="tab-menu">
                <div className="tab-menu-content">
                  <button 
                    className={`tab-button ${activeTab === 'home' ? 'active' : ''}`}
                    onClick={() => setActiveTab('home')}
                  >
                    Home
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                  >
                    Analytics
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('notifications')}
                  >
                    Notifications
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                  >
                    Settings
                  </button>
                </div>
              </div>
              <main className={`dashboard-container ${userEnvironment}`}>
                {userEnvironment === 'live' && (
                  <div className="live-mode-banner">
                    ⚠️ You are operating in LIVE mode. All checks will be performed against the real DVLA database.
                  </div>
                )}
                {activeTab === 'home' ? renderDashboardContent() : 
                 activeTab === 'analytics' ? <AnalyticsDashboard /> : 
                 activeTab === 'notifications' ? <NotificationsPage /> :
                 renderSettingsContent()}
              </main>
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <div className="App">
              <header className="app-header">
                <div className="header-content">
                  <h1>
                    {currentBusiness?.name ? 
                      `${currentBusiness.name} - DVLA Staff License Check Dashboard` : 
                      'Loading Dashboard...'}
                  </h1>
                  <div className="nav-links">
                    <div className="environment-indicator">
                      Environment: <span className={userEnvironment}>{userEnvironment.toUpperCase()}</span>
                    </div>
                    {userRole && (
                      <span className="user-role">Role: {userRole}</span>
                    )}
                    <NotificationCenter />
                    <button className="logout-btn" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              </header>
              
              <div className="tab-menu">
                <div className="tab-menu-content">
                  <button 
                    className="tab-button"
                    onClick={() => {
                      setActiveTab('home');
                      navigate('/dashboard');
                    }}
                  >
                    Home
                  </button>
                  <button 
                    className="tab-button"
                    onClick={() => {
                      setActiveTab('analytics');
                      navigate('/dashboard');
                    }}
                  >
                    Analytics
                  </button>
                  <button 
                    className="tab-button"
                    onClick={() => {
                      setActiveTab('settings');
                      navigate('/dashboard');
                    }}
                  >
                    Settings
                  </button>
                  <button 
                    className="tab-button active"
                  >
                    Notifications
                  </button>
                </div>
              </div>
              
              <main className={`dashboard-container ${userEnvironment}`}>
                {userEnvironment === 'live' && (
                  <div className="live-mode-banner">
                    ⚠️ You are operating in LIVE mode. All checks will be performed against the real DVLA database.
                  </div>
                )}
                <NotificationsPage />
              </main>
            </div>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
